import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import { useTriggerLoadWithInput } from "@redotech/react-util/load";
import { useStyleProperty } from "@redotech/react-util/style";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { SettingsContext } from "../contexts/settings";
import { PortalCustomizationStyleTag } from "./portal-customization-style-tag";
import { resolveCustomizationImage } from "./resolve-customization-field";

export interface CustomizationContextValue {
  customization: PortalCustomization | null;
  preview: boolean;
}

export const CustomizationContext = createContext<CustomizationContextValue>({
  customization: null,
  preview: false,
});

export interface CustomizationApplicationContextValue {
  shouldApplyCustomization: boolean;
}

export const CustomizationApplicationContext =
  createContext<CustomizationApplicationContextValue>({
    shouldApplyCustomization: true,
  });

export const portalCustomizationDisabledRouteHandle = {
  portalCustomizationDisabled: true,
} as const;

export function routeDisablesPortalCustomization(handle: unknown): boolean {
  if (
    typeof handle !== "object" ||
    handle === null ||
    !("portalCustomizationDisabled" in handle)
  ) {
    return false;
  }
  return handle.portalCustomizationDisabled === true;
}

export const CustomizationApplicationProvider = memo(
  function CustomizationApplicationProvider({
    children,
    shouldApplyCustomization = true,
  }: {
    children: ReactNode | ReactNode[];
    shouldApplyCustomization?: boolean;
  }) {
    const value = useMemo<CustomizationApplicationContextValue>(
      () => ({ shouldApplyCustomization }),
      [shouldApplyCustomization],
    );

    return (
      <CustomizationApplicationContext.Provider value={value}>
        {children}
      </CustomizationApplicationContext.Provider>
    );
  },
);

export const CustomizationProvider = memo(function CustomizationProvider({
  children,
  preview = false,
  customizationOverride,
}: {
  children: ReactNode | ReactNode[];
  preview?: boolean;
  /** Optional in-memory customization that bypasses the RPC fetch. Used by
   * the merchant-app portal builder to show unsaved edits live. */
  customizationOverride?: PortalCustomization | null;
}) {
  const rpcClient = useContext(RpcClientContext);
  const settings = useContext(SettingsContext);
  const { shouldApplyCustomization } = useContext(
    CustomizationApplicationContext,
  );

  const [customizationLoad, refresh] = useTriggerLoadWithInput(
    async (_: void) => {
      if (!rpcClient) {
        return null;
      }
      const response = await rpcClient.getPortalCustomization({});
      return response;
    },
  );

  useEffect(() => {
    if (!shouldApplyCustomization) {
      return;
    }
    if (customizationOverride !== undefined) {
      return;
    }
    if (!rpcClient) {
      return;
    }
    refresh();
  }, [refresh, customizationOverride, rpcClient, shouldApplyCustomization]);

  const customization: PortalCustomization | null = !shouldApplyCustomization
    ? null
    : customizationOverride !== undefined
      ? (customizationOverride ?? null)
      : (customizationLoad.value?.customization ?? null);
  const portalBuilderMigrated = shouldApplyCustomization
    ? (customizationLoad.value?.portalBuilderMigrated ?? false)
    : false;

  // Apply the favicon to `<head>` as a side effect.
  const faviconUrl = useMemo(
    () =>
      resolveCustomizationImage(
        customization?.branding?.faviconUrl,
        settings?.brandKit?.images.faviconUrl ||
          settings?.brandKit?.images.logoUrl,
      ),
    [customization, settings],
  );
  useEffect(() => {
    if (preview || !shouldApplyCustomization || !faviconUrl) {
      return;
    }
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/x-icon";
    link.href = faviconUrl;
    document.head.append(link);
    return () => {
      link.remove();
    };
  }, [faviconUrl, preview, shouldApplyCustomization]);

  const shouldApplyThemeVariables = !preview && shouldApplyCustomization;
  const themeVariableValue = (value: string | null | undefined) =>
    shouldApplyThemeVariables ? (value ?? "inherit") : "inherit";

  // In preview mode and on order tracking pages, force every document-level CSS
  // var to `inherit` so the surrounding shell doesn't theme the current page.
  useStyleProperty(
    document.documentElement,
    "--redo-primary-color",
    themeVariableValue(settings?.theme.button_background_color),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-text-color",
    themeVariableValue(settings?.theme.button_text_color),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-primary-button-color",
    themeVariableValue(settings?.theme.button_background_color),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-accent-color",
    themeVariableValue(settings?.theme.accent_color),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-accent-color-background",
    themeVariableValue(settings?.theme.accent_background_color),
  );
  useStyleProperty(
    document.documentElement,
    "--return-app-body-font-family",
    themeVariableValue(settings?.theme.body_font_family),
  );
  useStyleProperty(
    document.documentElement,
    "--return-app-title-font-family",
    themeVariableValue(settings?.theme.title_font_family),
  );

  const value = useMemo<CustomizationContextValue>(
    () => ({ customization, preview }),
    [customization, preview],
  );

  const newCustomCss = settings?.theme.new_custom_css;

  const hasLoadedOnce =
    !shouldApplyCustomization ||
    customizationOverride !== undefined ||
    customizationLoad.value !== undefined ||
    !!customizationLoad.error;

  // Prevents legacy customizations from briefly showing before, likely can be removed when we fully remove old styling in favor of new portal builder
  if (!hasLoadedOnce) {
    return null;
  }

  return (
    <CustomizationContext.Provider value={value}>
      {!preview &&
        shouldApplyCustomization &&
        !portalBuilderMigrated &&
        newCustomCss &&
        createPortal(<style>{newCustomCss}</style>, document.body)}
      {!preview && shouldApplyCustomization && <PortalCustomizationStyleTag />}
      {children}
    </CustomizationContext.Provider>
  );
});
